import { Box, Button, Stack, Typography, styled, useTheme } from '@mui/material';
import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SignInSignUpLayout from '../../../components/Layout/SignInSignUpLayout/SignInSignUpLayout';
import Spinner from '../../../components/Spinner';
import useRegisterUserQuestionsWithAnswer from '../../../hooks/useRegisterUserQuestionsWithAnswer';
import {
  resetUpdateUserAttributesSubmit,
  updateUserAtributes,
  updateUserAttributesSubmit,
} from '../../../redux/auth';
import capitalize from '../../../utils/capitalize';
import { contentAgresivo, contentConservador, contentModerado } from '../../../utils/constants';
import { IconsRiskProfile } from '../../../components/Layout/InternalLayout/StyledComponentsProfile';

const TitlePrincipalMuseo = styled(Typography)(({ theme }) => ({
  whiteSpace: 'pre-wrap',
  fontSynthesis: 'none',
  color: theme.palette.white.main,
  fontStyle: 'normal',
  fontFamily: 'Museo-Bold',
  letterSpacing: '0px',
  textDecoration: 'none',
  textTransform: 'none',
  margin: '0px',
}));

const ProfileResultQuestions = () => {
  const [
    doRegisterUserQuestions,
    { data, error: errorRegisterUserQuestions, loading: loadingRegisterUserQuestions },
  ] = useRegisterUserQuestionsWithAnswer();

  const successRegisterUserQuestions = data?.registerUserQuestionsWithAnswer;
  const maxWidthContainer = '1200px';
  const theme = useTheme();

  const { user, successUpdateUserAttributes, loadingUpdateUserAttributes } = useSelector(
    state => state.auth,
  );
  const { questions } = useSelector(state => state.question);
  const [onboardingSuccess, setOnboardingSuccess] = useState(false);
  const [responseQuestion, setResponseQuestion] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userRisk = user?.['custom:profile'] ? user['custom:profile'].toLowerCase() : '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (successUpdateUserAttributes && !loadingUpdateUserAttributes) {
      dispatch(resetUpdateUserAttributesSubmit());
    }
  }, [loadingUpdateUserAttributes, successUpdateUserAttributes]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (questions) {
      doRegisterUserQuestions({
        variables: {
          data: Object.values(questions),
        },
      })
        .then(response => {
          const responseData = response.data;
          setResponseQuestion(responseData);
          dispatch(
            updateUserAttributesSubmit({
              name: 'custom:profile',
              value: responseData.registerUserQuestionsWithAnswer,
            }),
          );
        })
        .catch(error => {});
    } else {
      navigate('/questions');
    }
  }, [JSON.stringify(questions)]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (successRegisterUserQuestions && !loadingRegisterUserQuestions) {
      dispatch(
        updateUserAtributes({
          'custom:profile': successRegisterUserQuestions,
        }),
      );
    }
  }, [successRegisterUserQuestions, loadingRegisterUserQuestions]);

  useEffect(() => {
    if (errorRegisterUserQuestions && !loadingRegisterUserQuestions) {
      message.error('Ha ocurrido un error al guardar las respuestas');
    }
  }, [errorRegisterUserQuestions, loadingRegisterUserQuestions]);

  useEffect(() => {
    if (
      user?.['custom:checkid-sign-doc'] &&
      user['custom:checkid-sign-doc'] === 'signature_success'
    ) {
      setOnboardingSuccess(true);
    }
  }, [user]);

  if (loadingRegisterUserQuestions) {
    return <Spinner />;
  }

  const questionResponse = responseQuestion?.registerUserQuestionsWithAnswer;

  return (
    <SignInSignUpLayout questionResult={true}>
      <Stack
        sx={{
          boxSizing: 'border-box',
          borderRadius: '24px',
          padding: '24px',
          position: 'relative',
          zIndex: 4,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: { xs: '0rem', md: '0rem' },
          marginTop: { xs: '-1rem', sm: '-4rem', md: '-3rem' },
          height: '100vh',
        }}
        width={{ xs: '95%', sm: '90%', md: '70%' }}
        maxWidth={maxWidthContainer}>
        <Box>
          <IconsRiskProfile
            sx={{
              width: 'clamp(5.1875rem, 1.4375rem + 16vw, 10.4375rem);',
            }}
            src={`/assets/images/stepRisk/${userRisk.toLowerCase()}.svg`}
          />
        </Box>
        <Stack
          sx={{
            textAlign: 'center',
            color: theme.palette.white.main,
            marginTop: '1rem',
            gap: 1,
          }}>
          <TitlePrincipalMuseo
            variant='h2'
            fontSize='clamp(1.6rem, -0.0714rem + 4.5714vw, 2.5rem);'>
            {capitalize(user.name)}
          </TitlePrincipalMuseo>
          <TitlePrincipalMuseo
            variant='h6'
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.5rem' },
            }}>
            Tu perfil de inversionista es
          </TitlePrincipalMuseo>
          <TitlePrincipalMuseo
            variant='h4'
            sx={{
              color: theme.palette.secondary.text,
              fontSize: { xs: '1.5rem', sm: '2.375rem' },
            }}>
            {capitalize(userRisk)}
          </TitlePrincipalMuseo>

          {questionResponse && (
            <TitlePrincipalMuseo
              sx={{
                fontFamily: 'Poppins',
                fontSize: { xs: '1rem', sm: '1.138rem' },
              }}>
              {userRisk === 'agresivo' && contentAgresivo}
              {userRisk === 'conservador' && contentConservador}
              {userRisk === 'moderado' && contentModerado}
            </TitlePrincipalMuseo>
          )}
        </Stack>
        <Stack direction={{ md: 'row', xs: 'column' }} gap={3} marginTop={4} width={'100%'}>
          {!onboardingSuccess && (
            <Button
              onClick={() => navigate('/completeregistration')}
              fullWidth
              sx={{
                background: theme.palette.secondary.text,
                color: theme.palette.primary.main,
                textTransform: 'none',
                padding: '12px 20px',
                '&:hover': {
                  backgroundColor: theme.palette.secondary.text,
                },
              }}>
              Completar mi registro
            </Button>
          )}

          <Button
            onClick={() => navigate('/portfolio')}
            fullWidth
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.white.main,
              outline: `1px solid ${theme.palette.white.main}`,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            }}>
            Ir a simular inversiones
          </Button>
        </Stack>
      </Stack>
    </SignInSignUpLayout>
  );
};

export default ProfileResultQuestions;
